import { API_ENDPOINTS } from "@/config/api";
import type {
  ExternalReviewAdmin,
  ExternalReviewBulkResult,
  ExternalReviewCount,
  ExternalReviewCreate,
  ExternalReviewListParams,
  SentimentUpdate,
} from "@/types/externalReview";
import { fetchApi } from "./api";

export type ExternalReviewCountParams = Pick<
  ExternalReviewListParams,
  "source_id" | "platform" | "has_text" | "has_sentiment" | "q" | "url"
>;

function buildListQuery(params?: ExternalReviewListParams): string {
  const search = new URLSearchParams();
  if (params?.source_id) search.set("source_id", params.source_id);
  if (params?.platform) search.set("platform", params.platform);
  if (params?.has_text != null) search.set("has_text", String(params.has_text));
  if (params?.has_sentiment != null)
    search.set("has_sentiment", String(params.has_sentiment));
  if (params?.q) search.set("q", params.q);
  if (params?.url) search.set("url", params.url);
  if (params?.sort) search.set("sort", params.sort);
  if (params?.limit != null) search.set("limit", String(params.limit));
  if (params?.offset != null) search.set("offset", String(params.offset));
  const query = search.toString();
  return query ? `?${query}` : "";
}

export async function listExternalReviews(
  params?: ExternalReviewListParams,
): Promise<ExternalReviewAdmin[]> {
  return fetchApi<ExternalReviewAdmin[]>(
    `${API_ENDPOINTS.externalReviews}${buildListQuery(params)}`,
  );
}

export async function getExternalReviewsCount(
  params?: ExternalReviewCountParams,
): Promise<ExternalReviewCount> {
  return fetchApi<ExternalReviewCount>(
    `${API_ENDPOINTS.externalReviewsCount}${buildListQuery(params)}`,
  );
}

export async function getExternalReview(
  reviewId: number,
): Promise<ExternalReviewAdmin> {
  return fetchApi<ExternalReviewAdmin>(API_ENDPOINTS.externalReview(reviewId));
}

export async function updateSentiment(
  reviewId: number,
  body: SentimentUpdate,
): Promise<ExternalReviewAdmin> {
  return fetchApi<ExternalReviewAdmin>(
    API_ENDPOINTS.externalReviewSentiment(reviewId),
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  );
}

export async function ingestExternalReview(
  sourceId: string,
  body: ExternalReviewCreate,
): Promise<ExternalReviewAdmin> {
  return fetchApi<ExternalReviewAdmin>(
    API_ENDPOINTS.sourceExternalReviews(sourceId),
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

export async function ingestExternalReviewsBulk(
  sourceId: string,
  reviews: ExternalReviewCreate[],
): Promise<ExternalReviewBulkResult> {
  return fetchApi<ExternalReviewBulkResult>(
    API_ENDPOINTS.sourceExternalReviewsBulk(sourceId),
    {
      method: "POST",
      body: JSON.stringify({ reviews }),
    },
  );
}
